import { useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Formik } from "formik";
import * as Yup from "yup";
import { useMutation } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";

import { resetPassword } from "src/api/auth";
import AuthField from "src/components/auth/AuthField";
import AuthHeading from "src/components/auth/AuthHeading";
import { Button } from "src/components/ui/button";
import { showSuccessToast } from "src/lib/utils";

const validationSchema = Yup.object().shape({
	token: Yup.string().required("Token is required"),
	password: Yup.string().min(8, "Password must be at least 8 characters").required("Password is required"),
	password_confirmation: Yup.string()
		.oneOf([Yup.ref("password")], "Passwords must match")
		.required("Confirm password is required"),
});

const ResetPassword = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	const token = searchParams.get("token");

	useEffect(() => {
		if (!token) navigate("/login");
	}, [token, navigate]);

	const { mutate, isLoading } = useMutation(resetPassword, {
		onSuccess: ({ message }) => {
			showSuccessToast(message || "Password reset successfully");
			navigate("/login");
		},
	});

	return (
		<>
			<AuthHeading title="Reset password" subtitle="Set up a new and secure password." />

			<Formik
				// The token comes from the reset link. It used to stay "" here while the schema
				// required it, so the form could never pass validation — seeded from the URL now.
				initialValues={{ token: token ?? "", password: "", password_confirmation: "" }}
				enableReinitialize
				validationSchema={validationSchema}
				onSubmit={(values) => mutate(values)}
			>
				{({ values, handleChange, handleBlur, handleSubmit, errors, touched }) => (
					<form onSubmit={handleSubmit} className="tw-space-y-4">
						<AuthField
							label="New Password"
							name="password"
							isPassword
							placeholder="At least 8 characters"
							value={values.password}
							onChange={handleChange}
							onBlur={handleBlur}
							error={touched.password && errors.password}
							autoComplete="new-password"
						/>

						<AuthField
							label="Confirm Password"
							name="password_confirmation"
							isPassword
							placeholder="Re-enter your password"
							value={values.password_confirmation}
							onChange={handleChange}
							onBlur={handleBlur}
							error={touched.password_confirmation && errors.password_confirmation}
							autoComplete="new-password"
						/>

						{errors.token && <p className="tw-text-[12.5px] tw-text-red">{errors.token}</p>}

						<Button type="submit" disabled={isLoading} className="tw-w-full tw-bg-teal tw-text-white hover:tw-bg-teal-dark">
							{isLoading ? "Saving..." : "Reset password"}
						</Button>

						<p className="tw-text-center">
							<Link
								to="/login"
								className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-[13px] tw-font-semibold tw-text-teal hover:tw-underline"
							>
								<ArrowLeft className="tw-h-3.5 tw-w-3.5" />
								Back to login
							</Link>
						</p>
					</form>
				)}
			</Formik>
		</>
	);
};

export default ResetPassword;
